import * as React from 'react';

import {
  Close as CloseIcon,
  Add as ZoomInIcon,
  Remove as ZoomOutIcon,
  RotateRight as RotateRightIcon,
} from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
} from '@mui/material';

import styles from './ProductCard.module.css';

interface Props {
  imageUrls: string[];
}

interface State {
  showDialog: boolean;
  selectedImage: string;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

interface ViewerState {
  zoom: number;
  rotation: number;
  panX: number;
  panY: number;
}

const initialViewerState: ViewerState = {
  zoom: 1,
  rotation: 0,
  panX: 0.5,
  panY: 0.5,
};

const clamp = (val: number, min: number, max: number) =>
  Math.min(Math.max(val, min), max);

/**
 * Compute the CSS transform string from viewer state.
 * When rotated 90/270°, applies a compensating scale so the image fits.
 */
function computeTransform(state: ViewerState): string {
  const { zoom, rotation, panX, panY } = state;
  const isOrthogonal = rotation % 180 !== 0;
  // Compensate for aspect ratio swap on 90/270 rotation
  const rotScale = isOrthogonal ? 0.75 : 1;
  const effectiveScale = zoom * rotScale;

  const tx = (0.5 - panX) * (zoom - 1) * 100;
  const ty = (0.5 - panY) * (zoom - 1) * 100;

  return `translate(${tx}%, ${ty}%) scale(${effectiveScale}) rotate(${rotation}deg)`;
}

interface RenderImageDialogProps {
  showDialog: boolean;
  setDialog: () => void;
  url: string | null;
}

const RenderImageDialog: React.FC<RenderImageDialogProps> = ({
  showDialog,
  setDialog,
  url,
}) => {
  const [viewer, setViewer] = React.useState<ViewerState>(initialViewerState);
  const [imgError, setImgError] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const rafRef = React.useRef<number | null>(null);
  const latestMouseRef = React.useRef<{ x: number; y: number } | null>(null);

  // Reset all state when dialog closes or URL changes
  React.useEffect(() => {
    setViewer(initialViewerState);
    setImgError(false);
  }, [showDialog, url]);

  // Attach wheel listener imperatively with { passive: false } to allow preventDefault
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || !showDialog) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      const pointerX = clamp((e.clientX - rect.left) / rect.width, 0, 1);
      const pointerY = clamp((e.clientY - rect.top) / rect.height, 0, 1);

      setViewer((prev) => {
        const nextZoom =
          e.deltaY < 0
            ? Math.min(prev.zoom * 1.3, MAX_ZOOM)
            : Math.max(prev.zoom / 1.3, MIN_ZOOM);
        return {
          ...prev,
          zoom: nextZoom,
          panX: pointerX,
          panY: pointerY,
        };
      });
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [showDialog]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (viewer.zoom <= MIN_ZOOM) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    latestMouseRef.current = {
      x: clamp((e.clientX - rect.left) / rect.width, 0, 1),
      y: clamp((e.clientY - rect.top) / rect.height, 0, 1),
    };

    if (rafRef.current !== null) return;

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const pos = latestMouseRef.current;
      if (!pos) return;
      setViewer((prev) => ({ ...prev, panX: pos.x, panY: pos.y }));
    });
  };

  const handleMouseLeave = () => {
    if (viewer.zoom <= MIN_ZOOM) return;
    setViewer((prev) => ({ ...prev, panX: 0.5, panY: 0.5 }));
  };

  const handleClick = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    const y = clamp((e.clientY - rect.top) / rect.height, 0, 1);

    setViewer((prev) =>
      prev.zoom > MIN_ZOOM
        ? { ...prev, zoom: MIN_ZOOM, panX: 0.5, panY: 0.5 }
        : { ...prev, zoom: 3, panX: x, panY: y },
    );
  };

  const handleZoomIn = () => {
    setViewer((prev) => ({
      ...prev,
      zoom: Math.min(prev.zoom * 1.5, MAX_ZOOM),
    }));
  };

  const handleZoomOut = () => {
    setViewer((prev) => ({
      ...prev,
      zoom: Math.max(prev.zoom / 1.5, MIN_ZOOM),
    }));
  };

  const handleRotate = () => {
    setViewer((prev) => ({
      ...prev,
      rotation: (prev.rotation + 90) % 360,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case '+':
      case '=':
        e.preventDefault();
        handleZoomIn();
        break;
      case '-':
        e.preventDefault();
        handleZoomOut();
        break;
      case 'r':
      case 'R':
        e.preventDefault();
        handleRotate();
        break;
      case '0':
        e.preventDefault();
        setViewer(initialViewerState);
        break;
    }
  };

  // Cleanup rAF on unmount
  React.useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const transform = computeTransform(viewer);

  return (
    <Dialog
      open={showDialog}
      onClose={setDialog}
      maxWidth={false}
      aria-labelledby="image-viewer-title"
    >
      <DialogTitle className={styles.dialogHeader} id="image-viewer-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={handleZoomOut}
            disabled={viewer.zoom <= MIN_ZOOM}
            size="small"
            aria-label="Zoom out"
          >
            <ZoomOutIcon />
          </IconButton>
          <span
            style={{ fontSize: 14, minWidth: 45, textAlign: 'center' }}
            aria-live="polite"
          >
            {Math.round(viewer.zoom * 100)}%
          </span>
          <IconButton
            onClick={handleZoomIn}
            disabled={viewer.zoom >= MAX_ZOOM}
            size="small"
            aria-label="Zoom in"
          >
            <ZoomInIcon />
          </IconButton>
          <IconButton
            onClick={handleRotate}
            size="small"
            aria-label="Rotate image clockwise"
          >
            <RotateRightIcon />
          </IconButton>
        </Box>
        <IconButton onClick={setDialog} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflow: 'hidden', padding: '16px !important' }}>
        <div
          ref={containerRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="img"
          aria-label="Zoomable product image"
          style={{
            width: '80vw',
            height: '75vh',
            maxWidth: 1000,
            maxHeight: 750,
            overflow: 'hidden',
            cursor: viewer.zoom > MIN_ZOOM ? 'crosshair' : 'zoom-in',
            position: 'relative',
            outline: 'none',
          }}
        >
          {url && !imgError ? (
            <img
              src={url}
              alt="Product image"
              draggable={false}
              onError={() => setImgError(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                transform,
                willChange: viewer.zoom > MIN_ZOOM ? 'transform' : 'auto',
                transition: 'none',
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                color: 'text.secondary',
                fontSize: 16,
              }}
            >
              {imgError ? 'Failed to load image' : 'No image available'}
            </Box>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default class RenderProductThumbnail extends React.Component<
  Props,
  State
> {
  state = {
    showDialog: false,
    selectedImage: null,
  };

  setDialog = () => {
    this.setState({
      showDialog: !this.state.showDialog,
    });
  };

  setSelectedImage = (url) => {
    this.setState({
      selectedImage: url,
    });
  };

  render() {
    const { imageUrls } = this.props;

    return (
      <div style={{ padding: '0 5px' }}>
        <div className={styles.thumbnailContainer}>
          <ImageList
            rowHeight={69}
            cols={2}
            gap={10}
            className={styles.thumbnailGrid}
          >
            {imageUrls.map((url, index) => (
              <ImageListItem key={index} cols={1}>
                <img
                  className={styles.thumbnail}
                  src={url}
                  alt={`image_${index}`}
                  onClick={() => {
                    this.setSelectedImage(url);
                    this.setDialog();
                  }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        </div>
        <RenderImageDialog
          showDialog={this.state.showDialog}
          setDialog={this.setDialog}
          url={this.state.selectedImage}
        />
      </div>
    );
  }
}
